import { Report, ReportResponse } from "services/types/reports.type";
import {
  convertCustomTestResponseToModel,
  convertReportResponseToModel
} from "utils/reports.utils";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import { Diagnosis } from "./types/diagnosis.type";
import type { PromptOutputFormat } from "./types/prompts.type";
import {
  CustomTest,
  CustomTestInput,
  CustomTestResponse,
  ReportFilters,
  ReportInput,
  ReportsResponse
} from "./types/reports.type";

export const GET_LIST_OF_REPORTS_DEFAULT_QUERY =
  "{id,type,is_signed,created_at,clinic{id,name,display_name},noah_session{session_date},signed_at,appointment{id,start_moment,clinic{name,display_name,timezone},staff_member{id,first_name,last_name,suffix,title},type{id,name}},report_date,created_by}";
export const GET_REPORT_DEFAULT_QUERY =
  "{id,otoacoustic_emissions_report,pure_tone_audiogram_report,speech_audiometry_report,word_recognition_report,additional_word_recognition_report,tympanometry_report,acoustic_reflex_report,acoustic_reflex_decay_report,speech_in_noise_report,audiometer,calibration_date,audiometer_configuration{id,audiometer,calibration_date,serial_number,title,is_active},referring_physician,primary_care_physician,summary,cover_letter,include_cover_letter,type,reliability,test_method,transducer,hearing_loss_left,hearing_loss_right,hearing_loss_type_left,hearing_loss_type_right,hearing_loss_shape_left,hearing_loss_shape_right,is_diagnosis_selected,is_hearing_aids_included,signed_at,is_signed,report_date,created_at,clinic{id,display_name,name,logo,timezone,street_address_1,street_address_2,city,state,zip_code,phone,practice{id,name,logo,timezone}},noah_session{session_date},appointment{id,clinic{id,display_name,name,logo,timezone,street_address_1,street_address_2,city,state,zip_code,phone,practice{id,name,logo,timezone}},start_moment,patient{noah_id,noah_patient_id},staff_member{id,first_name,last_name,suffix,title,signature,license_number},supervising_provider{id,first_name,last_name,suffix,title,signature,license_number},is_direct_supervisor,type{id,name,default_duration}},reviewer{id,first_name,last_name,suffix,title,signature,license_number},diagnosis{id,hearing_loss_left,hearing_loss_right,hearing_loss_type_left,hearing_loss_type_right,hearing_loss_shape_left,hearing_loss_shape_right,icd10_codes,recommended_icd10_codes},include_tympanometry_graph}";

// Minimal field set for the patient header / quickview / inbox-panel "last audiogram"
// summary. Only includes what those surfaces render: the report date, the pure-tone
// audiogram graph, and the speech/word-recognition tooltip. Avoids pulling the full
// report (all sub-reports + nested clinic/practice/appointment/reviewer/diagnosis),
// which is unnecessary outside the audiology section.
export const PATIENT_HEADER_AUDIOGRAM_QUERY =
  "{id,report_date,created_at,pure_tone_audiogram_report,speech_audiometry_report,word_recognition_report,additional_word_recognition_report,noah_session{session_date},appointment{start_moment,clinic{timezone}}}";

export const reportsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getReport: builder.query<Report, { reportId: number; query: string }>({
      query: ({ reportId, query = "{*}" }) => ({
        url: `/reports/${reportId}/?query=${query}`,
        headers: { "X-Reports-DB": "true" }
      }),
      providesTags: (result) => {
        return ["Reports", { type: "Report", id: result?.id }];
      },
      transformResponse: convertReportResponseToModel
    }),
    getReports: builder.query<Report[], ReportFilters>({
      query: ({
        patientId,
        appointmentId: appointment = "",
        type,
        limit = 100,
        offset = 0,
        ordering = "-id",
        has_audiogram,
        query = GET_LIST_OF_REPORTS_DEFAULT_QUERY
      }) => ({
        url: `/reports/?${formatValidQueryParamUrlCommaSeparated({
          patient: patientId,
          type,
          appointment,
          ordering,
          limit,
          has_audiogram,
          offset
        })}&query=${query}`,
        headers: { "X-Reports-DB": "true" }
      }),
      providesTags: (response, error, args) => [
        "Reports",
        { id: args.patientId, type: "Reports" }
      ],
      transformResponse: (response: ReportsResponse) =>
        response.map(convertReportResponseToModel)
    }),
    addReport: builder.mutation<Report, Partial<ReportInput>>({
      query: (reportData) => ({
        url: "/reports/",
        method: "POST",
        body: reportData
      }),
      invalidatesTags: ["Reports"]
    }),
    updateReport: builder.mutation<
      ReportResponse,
      Partial<ReportInput> & { id: number }
    >({
      query: ({ id, ...reportData }: ReportInput & { id: number }) => ({
        url: `/reports/${id}/`,
        method: "PATCH",
        body: reportData
      })
      //
      // Please do not invalidate tags here.
      // It refreshes the whole report in autosaving
      // which leads to really bad side effects
      //
      // invalidatesTags: (result) => {
      //   return [{ type: "Report", id: result?.id }];
      // },
    }),
    getReportCustomTests: builder.query<CustomTest[], number>({
      query: (reportId) => ({
        url: `/reports/${reportId}/custom-test-reports/?limit=300`,
        headers: { "X-Reports-DB": "true" }
      }),
      providesTags: ["CustomTests"],
      transformResponse: (response: CustomTestResponse[]) =>
        response.map(convertCustomTestResponseToModel)
    }),
    addCustomTest: builder.mutation<CustomTestResponse, CustomTestInput>({
      query: ({ report_id, ...customTestData }) => ({
        url: `/reports/${report_id}/custom-test-reports/`,
        method: "POST",
        body: customTestData
      }),
      invalidatesTags: ["CustomTests"]
    }),
    updateCustomTest: builder.mutation<
      CustomTestResponse,
      CustomTestInput & CustomTestResponse
    >({
      query: ({ report_id, id, ...customTestData }) => ({
        url: `/reports/${report_id}/custom-test-reports/${id}/`,
        method: "PATCH",
        body: customTestData
      }),
      invalidatesTags: ["CustomTests"]
    }),
    calculateReportDiagnosis: builder.mutation<Diagnosis, number>({
      query: (reportId) => ({
        url: `/reports/${reportId}/diagnosis/`,
        method: "PATCH"
      }),
      invalidatesTags: ["Appointments"]
    }),
    removeCustomTest: builder.mutation<void, { report_id: number; id: number }>(
      {
        query: ({ report_id, id }) => ({
          url: `/reports/${report_id}/custom-test-reports/${id}/`,
          method: "DELETE"
        }),
        invalidatesTags: ["CustomTests"]
      }
    ),
    deleteReport: builder.mutation<void, number>({
      query: (report_id) => ({
        url: `/reports/${report_id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Reports", "CustomTests"]
    }),
    syncReportFromNoah: builder.mutation<
      Report,
      { report_id: number; session_id?: number }
    >({
      query: ({ report_id, session_id }) => ({
        url: `/reports/${report_id}/sync`,
        params: {
          session_id
        },
        method: "PUT"
      }),
      invalidatesTags: (result, _, { session_id }) => {
        if (session_id) {
          return ["NoahSessions", "NoahDevices"];
        }

        return [];
      }
    }),
    autoCompleteReportSummary: builder.mutation<
      string,
      { report_id: number; user_prompt: string; format?: PromptOutputFormat }
    >({
      query: ({ report_id, user_prompt, format }) => ({
        url: `/reports/${report_id}/complete`,
        method: "POST",
        body: { user_prompt, ...(format && { format }) }
      })
    })
  })
});

export const {
  useGetReportQuery,
  useLazyGetReportQuery,
  useGetReportsQuery,
  useAddReportMutation,
  useUpdateReportMutation,
  useGetReportCustomTestsQuery,
  useAddCustomTestMutation,
  useUpdateCustomTestMutation,
  useRemoveCustomTestMutation,
  useSyncReportFromNoahMutation,
  useAutoCompleteReportSummaryMutation,
  useDeleteReportMutation,
  useCalculateReportDiagnosisMutation
} = reportsApi;
